/**
 * Hook to fetch and manage weekly status week assignment preview.
 *
 * Determines which week a status update will be assigned to based on submission day:
 * - Monday-Tuesday: Assign to previous week (unless previous week status exists)
 * - Wednesday-Sunday: Assign to current week
 */

import { useCallback, useEffect, useState } from 'react';

import {
  getWeeklyStatusAssignment,
  type WeeklyStatusAssignmentResponse,
} from '@/api/project-details/api';

export type WeeklyStatusAssignmentPreview = WeeklyStatusAssignmentResponse;

interface UseWeeklyStatusAssignmentReturn {
  assignment: WeeklyStatusAssignmentPreview | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch week assignment preview for the current user and project.
 * Cached for the session to avoid repeated API calls.
 */
export function useWeeklyStatusAssignment(
  clientId: string | undefined | null,
  projectId: string | undefined | null,
  accessToken: string | undefined | null,
): UseWeeklyStatusAssignmentReturn {
  const [assignment, setAssignment] = useState<WeeklyStatusAssignmentPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAssignment = useCallback(async () => {
    // Validate required parameters
    if (!clientId || !projectId || !accessToken) {
      setAssignment(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getWeeklyStatusAssignment(accessToken, clientId, projectId);
      setAssignment(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      setAssignment(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, projectId, accessToken]);

  useEffect(() => {
    fetchAssignment();
  }, [fetchAssignment]);

  return {
    assignment,
    loading,
    error,
    refetch: fetchAssignment,
  };
}
